/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { getKioskLock, setKioskLock } from "@/utils/kioskLock";
import { showToast } from "@/components/common/Toast";

export const KioskSettings: React.FC = () => {
  const [locked, setLocked] = useState(() => getKioskLock());

  const handleChange = (checked: boolean) => {
    setKioskLock(checked);
    setLocked(checked);
    showToast(checked ? "Kiosk editing locked" : "Kiosk editing unlocked", "success");
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <LockClosedIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          Kiosk
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Settings for Nyrvex running as a wall-mounted or dedicated display
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockClosedIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Lock Kiosk editing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
              Hides the Edit and Add Widget controls in Kiosk mode, so an iPad
              or other display mounted where anyone can touch it can't have
              its widgets rearranged, resized, or removed by accident. This
              is stored on this browser only -- exit Kiosk mode and come back
              here to unlock it again.
            </p>
            <Switch checked={locked} onCheckedChange={handleChange} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
